/*
 * Copyright Deskshare Inc.
 * All rights reserved.
 *
 * This software is proprietary and confidential.
 * Unauthorized copying, distribution, or use is strictly prohibited.
 */

/**
 * Cart Quantity Limits Configuration
 *
 * Provides centralized quantity validation for cart operations.
 * Default limit: 999 units per product — supports bulk and enterprise licensing.
 * Must stay in sync with backend/apps/checkout/constants.py DEFAULT_MAX_QUANTITY.
 *
 * Special products have custom limits:
 * - Document Translator: sold by word count, allows up to 1M words
 * - Free trial products: limited to 1 unit
 *
 * @module cart-limits
 */

import i18n from "@/i18n";
import {
  DOCUMENT_TRANSLATOR_PRODUCT_ID,
  TSL_EXTRA_WORDS_CONFIGURED,
  TSL_EXTRA_WORDS_PRODUCT_ID,
  TSL_TRIAL_PRODUCT_ID,
  TSL_45K_PRODUCT_ID,
  TSL_100K_PRODUCT_ID,
} from "@/config/product-ids";
// N-10 (S629): formatNumber reads the shop's active language. A bare
// toLocaleString() follows the BROWSER's language instead, so one page
// could show translated words around numbers grouped for a different
// country than the one the customer chose.
import { formatNumber } from "@/lib/active-locale";

/**
 * NF-1 (S576): pack rules, mirrored from backend/apps/core/constants.py.
 *
 * Declared here rather than beside the helpers below because the limits map is
 * built when this module loads and reads TSL_EXTRA_WORDS_MAX_PACKS. A const
 * declared after that point is in the temporal dead zone and throws on import.
 */
export const TSL_EXTRA_WORDS_PER_PACK = 10_000;
export const TSL_EXTRA_WORDS_MAX_PACKS = 10;

// ============================================================================
// Types
// ============================================================================

/**
 * Quantity limits for a product
 */
interface ProductLimits {
  /** Maximum allowed quantity */
  max: number;
  /** Minimum allowed quantity */
  min: number;
}

/**
 * Product-specific limit override configuration
 */
interface ProductLimitOverride {
  /** Maximum quantity for this product */
  maxQuantity: number;
  /** Minimum quantity for this product */
  minQuantity: number;
}

/**
 * Result of quantity validation
 */
interface QuantityValidationResult {
  /** Whether the quantity is within valid limits */
  isValid: boolean;
  /** Error message if invalid, null otherwise */
  error: string | null;
  /** The quantity value clamped to valid range */
  clampedValue: number;
  /** Whether the quantity hit the maximum limit */
  hitMaxLimit: boolean;
  /** Whether the quantity hit the minimum limit */
  hitMinLimit: boolean;
}

// ============================================================================
// Constants
// ============================================================================

/**
 * Default maximum quantity per product in a single self-serve order.
 *
 * Lowered from 999 to 100 in Session 556. Capping how many units one order may
 * contain is a standard fraud control: a stolen card is validated on a small
 * purchase and then used for a large one, and the largest charge the cart can
 * produce without anyone at Deskshare seeing it is the one worth limiting. The
 * licence keys are issued before a chargeback can arrive, so the loss on such
 * an order is the full amount plus the fee.
 *
 * It also keeps a single order comfortably below the licence server's
 * per-system activation ceiling, which the backend constant depends on.
 *
 * A customer who needs more than this is a volume-licensing conversation, and
 * the cart tells them so rather than simply refusing.
 *
 * Must stay in sync with backend/apps/checkout/constants.py DEFAULT_MAX_QUANTITY.
 */
export const DEFAULT_MAX_QUANTITY = 100;

/**
 * Default minimum quantity per product.
 * Standard cart behavior - at least 1 item required.
 */
const MIN_QUANTITY = 1;

/**
 * Product-specific quantity limit overrides.
 * Keys are resolved from env-backed constants in config/product-ids.ts.
 */
const PRODUCT_OVERRIDES: Record<string, ProductLimitOverride> = {
  // Document Translator - sold by word count
  [DOCUMENT_TRANSLATOR_PRODUCT_ID]: {
    maxQuantity: 1000000, // 1 million words max per order
    minQuantity: 1,
  },

  // Text Speaker Live Trial - limited to 1 per customer
  [TSL_TRIAL_PRODUCT_ID]: {
    maxQuantity: 1,
    minQuantity: 1,
  },

  // Text Speaker Live - 45,000 words monthly subscription
  [TSL_45K_PRODUCT_ID]: {
    maxQuantity: 1,
    minQuantity: 1,
  },

  // Text Speaker Live - 100,000 words monthly subscription
  [TSL_100K_PRODUCT_ID]: {
    maxQuantity: 1,
    minQuantity: 1,
  },
  // NF-1 (S576): Extra Words is the one Text Speaker Live product not locked to
  // a quantity of 1. The others are subscriptions -- a customer cannot hold two
  // of the same plan. This is a consumable, bought in packs.
  [TSL_EXTRA_WORDS_PRODUCT_ID]: {
    maxQuantity: TSL_EXTRA_WORDS_MAX_PACKS,
    minQuantity: 1,
  },
};

// ============================================================================
// Functions
// ============================================================================

/**
 * Get quantity limits for a specific product.
 *
 * Checks for product-specific overrides, falls back to defaults.
 *
 * @param productId - Stripe product ID
 * @returns Object with max and min quantity limits
 */
export function getProductLimits(productId: string): ProductLimits {
  const override = PRODUCT_OVERRIDES[productId];

  return {
    max: override?.maxQuantity ?? DEFAULT_MAX_QUANTITY,
    min: override?.minQuantity ?? MIN_QUANTITY,
  };
}

/**
 * Get effective quantity limits for a product.
 *
 * Thin wrapper around getProductLimits retained as the public API surface
 * for cart components.
 *
 * @param productId - Stripe product ID
 * @returns Object with max and min quantity limits
 */
export function getEffectiveProductLimits(productId: string): ProductLimits {
  return getProductLimits(productId);
}

/**
 * Validate a quantity against product limits.
 *
 * @param quantity - Quantity to validate
 * @param productId - Stripe product ID (for limit lookup)
 * @returns Validation result with isValid, error, and clampedValue
 */
export function validateQuantity(
  quantity: number,
  productId: string,
): QuantityValidationResult {
  const limits = getProductLimits(productId);

  // Handle NaN, undefined, or non-numeric input
  if (isNaN(quantity) || quantity === null || quantity === undefined) {
    return {
      isValid: false,
      error: i18n.t("cart.quantity.invalid"),
      clampedValue: limits.min,
      hitMaxLimit: false,
      hitMinLimit: true,
    };
  }

  // Handle non-integer input (decimals)
  if (!Number.isInteger(quantity)) {
    const rounded = Math.round(quantity);
    const clamped = Math.max(limits.min, Math.min(limits.max, rounded));
    return {
      isValid: false,
      error: i18n.t("cart.quantity.notWholeNumber"),
      clampedValue: clamped,
      hitMaxLimit: clamped === limits.max,
      hitMinLimit: clamped === limits.min,
    };
  }

  // Check minimum limit
  if (quantity < limits.min) {
    return {
      isValid: false,
      error: i18n.t("cart.quantity.minimumIs", { min: limits.min }),
      clampedValue: limits.min,
      hitMaxLimit: false,
      hitMinLimit: true,
    };
  }

  // Check maximum limit
  if (quantity > limits.max) {
    return {
      isValid: false,
      error: i18n.t("cart.quantity.maximumIs", { max: limits.max }),
      clampedValue: limits.max,
      hitMaxLimit: true,
      hitMinLimit: false,
    };
  }

  // Valid quantity
  return {
    isValid: true,
    error: null,
    clampedValue: quantity,
    hitMaxLimit: quantity === limits.max,
    hitMinLimit: quantity === limits.min,
  };
}

/**
 * Check if a product is a free trial product.
 *
 * IMPORTANT: Must check explicit product IDs, not quantity limits.
 * Many non-trial products (e.g., monthly subscriptions) also have max=1.
 *
 * @param productId - Stripe product ID
 * @returns True if product is a free trial
 */
export function isTrialProduct(productId: string): boolean {
  return productId === TSL_TRIAL_PRODUCT_ID;
}

/**
 * Check if a product is the Document Translator (special word count handling).
 *
 * @param productId - Stripe product ID
 * @returns True if product is Document Translator
 */
export function isDocumentTranslatorProduct(productId: string): boolean {
  return productId === DOCUMENT_TRANSLATOR_PRODUCT_ID;
}

/**
 * Check if a product is any Text Speaker Live variant.
 *
 * Matches the monthly subscription 45K plan and the 100K plan.
 * The one-time Extra Words add-on was removed in Session 257; its product ID
 * is still included here so that any legacy cart URL carrying that ID is
 * correctly isolated to a standalone checkout (cart isolation safety rule).
 *
 * @param productId - Stripe product ID
 * @returns True if product is Text Speaker Live
 */
export function isTextSpeakerLiveProduct(productId: string): boolean {
  return (
    productId === TSL_45K_PRODUCT_ID ||
    productId === TSL_100K_PRODUCT_ID ||
    productId === TSL_EXTRA_WORDS_PRODUCT_ID
  );
}

/**
 * Check if a product requires a standalone checkout cart (Part 4
 * "Cart Isolation Rules" of the Complete Shopping Cart Purchase Process
 * Documentation): "Products that need a standalone checkout (Document
 * Translator, Text Speaker Live) automatically clear the cart before loading."
 *
 * @param productId - Stripe product ID
 * @returns True if the product mandates cart isolation
 */
export function isStandaloneCartProduct(productId: string): boolean {
  return isDocumentTranslatorProduct(productId) || isTextSpeakerLiveProduct(productId);
}

/**
 * Get a user-friendly label for quantity limits.
 * Used for accessibility and tooltip text.
 *
 * @param productId - Stripe product ID
 * @returns Human-readable limit description
 */
/**
 * True for the two Text Speaker Live PLAN products -- the ones a plan upgrade
 * can be an upgrade of.
 *
 * VM-2 (FCR S637). The shop used to infer a Text Speaker Live plan upgrade from
 * nothing but the presence of a licence key, and a licence key is not evidence
 * of an upgrade: every returning customer of every desktop product has one. The
 * inference was written when Text Speaker Live was the only product that opened
 * the cart with an encrypted payload, and it was never narrowed when Document
 * Translator started doing the same.
 *
 * Extra Words is deliberately NOT in this list. A pack of words is added to a
 * plan; it is not a plan, and it is never the thing an upgrade upgrades.
 *
 * Gating on the product means it no longer matters how the address was built. A
 * Document Translator cart is never a Text Speaker Live upgrade, whether the
 * address came from the desktop, from a legacy build, or from somebody's hand.
 */
export function isTslPlanUpgradeProduct(productId: string): boolean {
  return productId === TSL_45K_PRODUCT_ID || productId === TSL_100K_PRODUCT_ID;
}

/** True for the one-time Extra Words pack product. */
export function isTslExtraWordsProduct(productId: string): boolean {
  return productId === TSL_EXTRA_WORDS_PRODUCT_ID;
}

/**
 * True for a product sold as credits that are spent on use: Document Translator
 * and the Extra Words pack.
 *
 * Session 752 N-1/N-2. The browser's copy of the server's
 * is_consumable_product() (backend/apps/checkout/constants.py), so "which
 * products carry no money-back guarantee and get no abandoned-cart reminder" is
 * decided in one place on each side. The refund endpoint refuses every cash
 * refund for these products, so the cart must never promise one for them.
 *
 * What must still NOT count as consumable (pinned by
 * src/lib/__tests__/s753-consumable-products.test.ts): the two Text Speaker
 * Live plans, the trial, and every licence product -- each keeps its
 * money-back line and its reminders.
 */
export function isConsumableProduct(productId: string): boolean {
  return isDocumentTranslatorProduct(productId) || isTslExtraWordsProduct(productId);
}

/**
 * True when Extra Words may be offered for sale.
 *
 * D-1 (S576): separate from isTslExtraWordsProduct on purpose. One asks "is
 * this that product"; this one asks "may we sell it". Until the live product
 * identifier is confirmed, the answer to the second is no -- see
 * TSL_EXTRA_WORDS_CONFIGURED.
 */
export function canSellExtraWords(): boolean {
  return TSL_EXTRA_WORDS_CONFIGURED;
}

export function getQuantityLimitLabel(productId: string): string {
  const limits = getProductLimits(productId);

  if (isTrialProduct(productId)) {
    return i18n.t("cart.quantity.trialLimitLabel");
  }

  if (isDocumentTranslatorProduct(productId)) {
    return i18n.t("common.wordRangeWords", {
      min: formatNumber(limits.min),
      max: formatNumber(limits.max),
    });
  }

  // NF-1 (S576): a customer buying words has no reason to know what a pack
  // holds. "Quantity: 1 - 10" says nothing about what they are choosing, so say
  // it here and on the cart line, and the number on screen means something.
  if (isTslExtraWordsProduct(productId)) {
    return i18n.t("common.wordRangePacks", {
      min: limits.min,
      max: limits.max,
      perPack: formatNumber(TSL_EXTRA_WORDS_PER_PACK),
    });
  }

  return `Quantity: ${limits.min} - ${limits.max}`;
}
